import React, { useState, useEffect, useCallback } from 'react';
import API_URL from '../config/API_URL';

const POLY_API = API_URL;
const POLL_INTERVAL = 30000;

const fonts = `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');`;

/* ══════════════════════════════════════
   Color palette
   ══════════════════════════════════════ */

const C = {
    bgPrimary: '#0a0e17', bgSecondary: '#111827', bgCard: '#1a1f2e', bgCardHover: '#222839',
    border: '#2a3040', borderGlow: 'rgba(99,102,241,0.2)',
    textPrimary: '#e2e8f0', textSecondary: '#94a3b8', textMuted: '#64748b',
    green: '#22c55e', greenDim: 'rgba(34,197,94,0.15)',
    red: '#ef4444', redDim: 'rgba(239,68,68,0.15)',
    cyan: '#38bdf8', cyanDim: 'rgba(56,189,248,0.12)',
    orange: '#f97316', orangeDim: 'rgba(249,115,22,0.15)',
    purple: '#a78bfa', purpleDim: 'rgba(167,139,250,0.15)',
    yellow: '#facc15', yellowDim: 'rgba(250,204,21,0.12)',
    indigo: '#6366f1', indigoDim: 'rgba(99,102,241,0.15)',
    fontSans: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontMono: "'JetBrains Mono', 'Fira Code', monospace",
};

const dirColors = {
    up: { bg: C.greenDim, color: C.green },
    down: { bg: C.redDim, color: C.red },
    neutral: { bg: C.yellowDim, color: C.yellow },
};

const assetColors = {
    BTC: { bg: C.orangeDim, color: C.orange },
    ETH: { bg: C.cyanDim, color: C.cyan },
};

/* ══════════════════════════════════════
   Styles
   ══════════════════════════════════════ */

const S = {
    page: { minHeight: '100vh', background: C.bgPrimary, color: C.textPrimary, fontFamily: C.fontSans, padding: '24px 32px 64px', boxSizing: 'border-box' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
    h1: { fontSize: '1.65rem', fontWeight: 700, color: C.textPrimary, margin: 0, letterSpacing: '-0.5px' },
    headerRight: { display: 'flex', alignItems: 'center', gap: 12 },
    lastUpdated: { fontSize: '0.75rem', color: C.textMuted, fontFamily: C.fontMono },

    badge: (color, bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', background: bg, color }),
    liveDot: (active) => ({ width: 10, height: 10, borderRadius: '50%', background: active ? C.green : C.red, boxShadow: `0 0 8px ${active ? C.green : C.red}`, animation: active ? 'poly-pulse 2s ease-in-out infinite' : 'none' }),

    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 },
    statCard: { background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', transition: 'border-color 0.3s, transform 0.2s' },
    statLabel: { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: C.textMuted, marginBottom: 6, fontWeight: 500 },
    statValue: (kind) => ({ fontSize: '1.5rem', fontWeight: 700, fontFamily: C.fontMono, color: kind === 'positive' ? C.green : kind === 'negative' ? C.red : C.textPrimary }),
    statSub: { fontSize: '0.72rem', color: C.textMuted, marginTop: 4 },

    columns: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 },
    card: { background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 },
    cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${C.border}`, background: C.bgSecondary },
    cardHeaderH2: { fontSize: '0.9rem', fontWeight: 600, margin: 0, color: C.textPrimary, display: 'flex', alignItems: 'center', gap: 8 },
    cardBody: { padding: '16px 20px' },

    riskGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 },
    riskItem: { display: 'flex', flexDirection: 'column', gap: 2 },
    riskLabel: { fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.7px', color: C.textMuted, fontWeight: 500 },
    riskValue: { fontFamily: C.fontMono, fontSize: '0.95rem', fontWeight: 600, color: C.textPrimary },

    predContainer: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
    predCard: { background: C.bgSecondary, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px' },
    predHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    predAsset: { fontSize: '1.15rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 },
    predDir: (dir) => ({ display: 'inline-flex', alignItems: 'center', padding: '4px 12px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', fontFamily: C.fontMono, letterSpacing: '0.5px', background: (dirColors[dir] || dirColors.neutral).bg, color: (dirColors[dir] || dirColors.neutral).color }),
    predStats: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 },
    predStatLabel: { fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.7px', color: C.textMuted, fontWeight: 500 },
    predStatValue: { fontFamily: C.fontMono, fontSize: '1rem', fontWeight: 700, color: C.textPrimary },

    signalRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
    signalName: { width: 120, fontSize: '0.68rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 500, flexShrink: 0 },
    signalBarContainer: { flex: 1, height: 16, background: C.bgPrimary, borderRadius: 4, position: 'relative', overflow: 'hidden' },
    signalBarCenter: { position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: C.border },
    signalVal: { width: 52, textAlign: 'right', fontSize: '0.72rem', fontWeight: 600, fontFamily: C.fontMono },

    metaGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 },
    metaItem: { background: C.bgSecondary, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', textAlign: 'center' },
    metaLabel: { fontSize: '0.68rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 500 },
    metaValue: { fontFamily: C.fontMono, fontSize: '1.25rem', fontWeight: 700, marginTop: 4, color: C.textPrimary },

    wrRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 },
    wrLabel: { width: 72, fontSize: '0.78rem', fontWeight: 600, textTransform: 'capitalize', color: C.textPrimary },
    wrBarBg: { flex: 1, height: 24, background: C.bgPrimary, borderRadius: 6, overflow: 'hidden', position: 'relative' },
    wrBarFill: (pct) => ({ height: '100%', borderRadius: 6, width: `${pct}%`, background: `linear-gradient(90deg, ${C.indigo}, ${C.purple})`, transition: 'width 0.6s ease' }),
    wrStat: { minWidth: 100, textAlign: 'right', fontSize: '0.78rem', color: C.textMuted, fontFamily: C.fontMono },

    costRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.border}`, fontSize: '0.85rem' },
    costLabel: { color: C.textMuted },
    costValue: { fontWeight: 600, fontFamily: C.fontMono },

    scrollArea: { maxHeight: 500, overflowY: 'auto', background: C.bgCard },
    obsItem: { background: C.bgSecondary, borderRadius: 10, padding: '14px 18px', marginBottom: 10, borderLeft: `3px solid ${C.indigo}`, transition: 'background 0.2s' },
    obsHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
    obsCycle: { fontWeight: 700, fontSize: '0.85rem', fontFamily: C.fontMono },
    obsTime: { fontSize: '0.72rem', color: C.textMuted, fontFamily: C.fontMono },
    obsMeta: { fontSize: '0.75rem', color: C.textMuted, marginBottom: 6 },
    obsPreds: { display: 'flex', gap: 10, flexWrap: 'wrap' },

    loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 20 },
    spinner: { width: 48, height: 48, border: `3px solid ${C.border}`, borderTopColor: C.indigo, borderRadius: '50%', animation: 'poly-spin 0.8s linear infinite' },
    errorWrap: { textAlign: 'center', padding: 48, color: C.red },
    retryBtn: { marginTop: 16, padding: '8px 24px', background: C.redDim, color: C.red, border: `1px solid ${C.red}`, borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
    noData: { color: C.textMuted, textAlign: 'center', padding: '32px 0' },
};

/* ══════════════════════════════════════
   Keyframes injection
   ══════════════════════════════════════ */

const KEYFRAMES_ID = 'poly-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(KEYFRAMES_ID)) {
    const style = document.createElement('style');
    style.id = KEYFRAMES_ID;
    style.textContent = `
    ${fonts}
    @keyframes poly-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.3)} }
    @keyframes poly-spin { to{transform:rotate(360deg)} }
    @keyframes poly-fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
    .poly-scroll::-webkit-scrollbar { width:6px }
    .poly-scroll::-webkit-scrollbar-track { background:transparent }
    .poly-scroll::-webkit-scrollbar-thumb { background:${C.border}; border-radius:999px }
    .poly-stat-hover:hover { border-color:${C.borderGlow} !important; transform:translateY(-2px) }
    .poly-obs-hover:hover { background:${C.bgCardHover} !important }
  `;
    document.head.appendChild(style);
}

/* ══════════════════════════════════════
   Helpers
   ══════════════════════════════════════ */

function formatTime(v) {
    if (!v) return '—';
    try {
        const d = typeof v === 'number' ? new Date(v * 1000) : new Date(v);
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    } catch { return String(v); }
}

function formatDateTime(v) {
    if (!v) return '—';
    try {
        const d = typeof v === 'number' ? new Date(v * 1000) : new Date(v);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return String(v); }
}

function pnlKind(v) { if (v == null || v === 0) return 'neutral'; return Number(v) >= 0 ? 'positive' : 'negative'; }
function pnlColor(v) { if (v == null) return C.textMuted; return Number(v) >= 0 ? C.green : C.red; }
function pct(v) { return v == null ? '—' : `${(v * 100).toFixed(1)}%`; }

/* ══════════════════════════════════════
   Sub-components
   ══════════════════════════════════════ */

function StatCard({ label, value, kind, sub }) {
    return (
        <div style={S.statCard} className="poly-stat-hover">
            <div style={S.statLabel}>{label}</div>
            <div style={S.statValue(kind)}>{value}</div>
            {sub && <div style={S.statSub}>{sub}</div>}
        </div>
    );
}

function RiskItem({ label, value, color }) {
    return (
        <div style={S.riskItem}>
            <span style={S.riskLabel}>{label}</span>
            <span style={{ ...S.riskValue, color: color || C.textPrimary }}>{value}</span>
        </div>
    );
}

function SignalBar({ name, value }) {
    const absVal = Math.abs(value);
    const widthPct = (absVal * 50).toFixed(1);
    const isPos = value >= 0;
    return (
        <div style={S.signalRow}>
            <span style={S.signalName}>{name.replace(/_/g, ' ')}</span>
            <div style={S.signalBarContainer}>
                <div style={S.signalBarCenter} />
                <div style={{
                    position: 'absolute',
                    top: 2, bottom: 2,
                    borderRadius: 3,
                    transition: 'all 0.4s ease',
                    width: `${widthPct}%`,
                    ...(isPos
                        ? { left: '50%', background: `linear-gradient(90deg, ${C.green}, #4ade80)` }
                        : { right: '50%', background: `linear-gradient(270deg, ${C.red}, #f87171)` }
                    ),
                }} />
            </div>
            <span style={{ ...S.signalVal, color: isPos ? C.green : C.red }}>
                {value.toFixed(3)}
            </span>
        </div>
    );
}

function PredictionCard({ asset, pred }) {
    const signals = pred.signals || {};
    const ac = assetColors[asset] || { bg: C.indigoDim, color: C.indigo };
    return (
        <div style={S.predCard}>
            <div style={S.predHeader}>
                <span style={S.predAsset}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, fontFamily: C.fontMono, background: ac.bg, color: ac.color }}>{asset}</span>
                </span>
                <span style={S.predDir(pred.direction)}>
                    {pred.direction === 'up' ? '▲' : pred.direction === 'down' ? '▼' : '—'} {pred.direction}
                </span>
            </div>
            <div style={S.predStats}>
                <div><div style={S.predStatLabel}>Confidence</div><div style={S.predStatValue}>{pct(pred.confidence)}</div></div>
                <div><div style={S.predStatLabel}>Composite</div><div style={S.predStatValue}>{pred.composite_score?.toFixed(4) ?? '—'}</div></div>
                <div><div style={S.predStatLabel}>Signals Aligned</div><div style={S.predStatValue}>{pred.signals_aligned ?? '—'}</div></div>
                <div><div style={S.predStatLabel}>Volatility</div><div style={S.predStatValue}>{pct(pred.volatility_pct)}</div></div>
            </div>
            {Object.entries(signals).map(([k, v]) => (
                <SignalBar key={k} name={k} value={v.value} />
            ))}
        </div>
    );
}

/* ══════════════════════════════════════
   Main component
   ══════════════════════════════════════ */

function PolyPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDashboard = useCallback(async () => {
        try {
            const res = await fetch(`${POLY_API}/api/poly/dashboard?limit=30`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setData(json);
            setError(null);
        } catch (err) {
            console.error('Poly fetch error:', err);
            if (!data) setError(err.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { fetchDashboard(); const id = setInterval(fetchDashboard, POLL_INTERVAL); return () => clearInterval(id); }, [fetchDashboard]);

    if (loading && !data) return (
        <div style={S.page}>
            <div style={S.loadingWrap}>
                <div style={S.spinner} />
                <span style={{ color: C.textMuted }}>Connecting to Polymarket bot…</span>
            </div>
        </div>
    );

    if (error && !data) return (
        <div style={S.page}>
            <div style={S.errorWrap}>
                <h2>⚠ Connection Failed</h2>
                <p>{error}</p>
                <button style={S.retryBtn} onClick={() => { setLoading(true); fetchDashboard(); }}>Retry</button>
            </div>
        </div>
    );

    const { status, meta_state, observations } = data || {};
    const s = status || {};
    const meta = meta_state || {};
    const obs = observations || [];
    const risk = s.risk || {};
    const cost = s.cost || {};
    const wr = s.win_rates || {};
    const latestObs = obs.length > 0 ? obs[0] : null;
    const preds = latestObs ? latestObs.predictions || {} : {};

    const canTrade = risk.can_trade;
    const balance = s.balance;
    const dailyPnl = risk.daily_pnl;

    return (
        <div style={{ ...S.page, animation: 'poly-fadeIn 0.35s ease forwards' }}>

            {/* ════ HEADER ════ */}
            <header style={S.header}>
                <div style={S.headerLeft}>
                    <h1 style={S.h1}>⚡ Polymarket Bot</h1>
                    <span style={S.badge(s.mode === 'live' ? C.green : C.yellow, s.mode === 'live' ? C.greenDim : C.yellowDim)}>
                        <span style={S.liveDot(s.mode === 'live')} />
                        {s.mode || '?'}
                    </span>
                    <span style={{ ...S.badge(C.indigo, C.indigoDim), fontFamily: C.fontMono }}>{s.strategy || '?'}</span>
                </div>
                <div style={S.headerRight}>
                    <span style={S.lastUpdated}>Updated: {formatTime(s.updated_at)}</span>
                </div>
            </header>

            {/* ════ STAT CARDS ════ */}
            <div style={S.statsRow}>
                <StatCard label="Balance" value={`$${(balance || 0).toFixed(2)}`} kind={pnlKind(balance)} sub={`${risk.survival_tier || '—'} tier`} />
                <StatCard label="Daily P&L" value={`${(dailyPnl || 0) >= 0 ? '+' : ''}$${(dailyPnl || 0).toFixed(4)}`} kind={pnlKind(dailyPnl)} sub={`${(risk.daily_pnl_pct || 0).toFixed(2)}% of balance`} />
                <StatCard label="Trades Today" value={String(risk.trades_today || 0)} kind="neutral" sub={`${risk.open_positions || 0} open · ${risk.consecutive_losses || 0} consec losses`} />
                <StatCard label="Can Trade" value={canTrade ? '✓ YES' : '✗ NO'} kind={canTrade ? 'positive' : 'negative'} sub={risk.block_reason || 'No blocks'} />
            </div>

            {/* ════ WIN RATES + RISK/COSTS ════ */}
            <div style={S.columns}>
                <div style={S.card}>
                    <div style={S.cardHeader}><h2 style={S.cardHeaderH2}>📊 Win Rates</h2></div>
                    <div style={S.cardBody}>
                        {Object.entries(wr).map(([key, val]) => (
                            <div style={S.wrRow} key={key}>
                                <span style={S.wrLabel}>{key}</span>
                                <div style={S.wrBarBg}>
                                    <div style={S.wrBarFill(val.total > 0 ? val.win_rate * 100 : 0)} />
                                </div>
                                <span style={S.wrStat}>{val.wins}W / {val.losses}L ({val.total > 0 ? (val.win_rate * 100).toFixed(0) : 0}%)</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={S.card}>
                    <div style={S.cardHeader}><h2 style={S.cardHeaderH2}>🛡️ Risk & Costs</h2></div>
                    <div style={S.cardBody}>
                        <div style={S.riskGrid}>
                            <RiskItem label="Daily Cost" value={cost.daily_cost || '$0'} />
                            <RiskItem label="Budget Used" value={cost.budget_used_pct || '0%'} />
                            <RiskItem label="Daily Budget" value={cost.daily_budget || '$0'} />
                            <RiskItem label="API Calls" value={String(cost.calls_today || 0)} />
                            <RiskItem label="Total Cost" value={cost.total_cost || '$0'} />
                            <RiskItem label="Open Positions" value={String(risk.open_positions || 0)} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ════ LATEST PREDICTIONS ════ */}
            <div style={S.card}>
                <div style={S.cardHeader}>
                    <h2 style={S.cardHeaderH2}>🎯 Latest Predictions {latestObs && <span style={{ fontSize: '0.72rem', color: C.textMuted, fontWeight: 400, fontFamily: C.fontMono }}>(Cycle {latestObs.cycle})</span>}</h2>
                    {latestObs && <span style={S.lastUpdated}>{formatTime(latestObs.timestamp)}</span>}
                </div>
                <div style={S.cardBody}>
                    {Object.keys(preds).length === 0
                        ? <p style={S.noData}>No predictions yet</p>
                        : <div style={S.predContainer}>
                            {Object.entries(preds).map(([asset, pred]) => (
                                <PredictionCard key={asset} asset={asset} pred={pred} />
                            ))}
                        </div>
                    }
                </div>
            </div>

            {/* ════ META-AGENT + OBSERVATION HISTORY ════ */}
            <div style={S.columns}>
                <div style={S.card}>
                    <div style={S.cardHeader}><h2 style={S.cardHeaderH2}>🤖 Meta-Agent</h2></div>
                    <div style={S.cardBody}>
                        <div style={S.metaGrid}>
                            <div style={S.metaItem}><div style={S.metaLabel}>Cycles</div><div style={S.metaValue}>{meta.cycles_observed || 0}</div></div>
                            <div style={S.metaItem}><div style={S.metaLabel}>Trades Seen</div><div style={S.metaValue}>{meta.trades_seen || 0}</div></div>
                            <div style={S.metaItem}><div style={S.metaLabel}>Weight Adj.</div><div style={S.metaValue}>{meta.weight_adjustments || 0}</div></div>
                            <div style={S.metaItem}><div style={S.metaLabel}>Code Props</div><div style={S.metaValue}>{meta.code_proposals || 0}</div></div>
                            <div style={S.metaItem}><div style={S.metaLabel}>Total Cost</div><div style={S.metaValue}>${(meta.total_api_cost || 0).toFixed(2)}</div></div>
                            <div style={S.metaItem}>
                                <div style={S.metaLabel}>Enabled</div>
                                <div style={{ ...S.metaValue, color: meta.enabled ? C.green : C.red }}>{meta.enabled ? '✓' : '✗'}</div>
                            </div>
                        </div>
                        <div style={{ marginTop: 14, fontSize: '0.75rem', color: C.textMuted, fontFamily: C.fontMono }}>
                            Last run: {formatDateTime(meta.last_run)}
                        </div>
                    </div>
                </div>

                <div style={S.card}>
                    <div style={S.cardHeader}>
                        <h2 style={S.cardHeaderH2}>📜 Observation History</h2>
                        <span style={S.lastUpdated}>{obs.length} cycles</span>
                    </div>
                    <div style={{ ...S.cardBody, padding: 0 }}>
                        <div style={{ ...S.scrollArea, padding: '12px 16px' }} className="poly-scroll">
                            {obs.length === 0
                                ? <p style={S.noData}>No observations yet</p>
                                : obs.map((o, i) => {
                                    const op = o.predictions || {};
                                    return (
                                        <div style={S.obsItem} key={i} className="poly-obs-hover">
                                            <div style={S.obsHeader}>
                                                <span style={S.obsCycle}>Cycle {o.cycle}</span>
                                                <span style={S.obsTime}>{formatDateTime(o.timestamp)}</span>
                                            </div>
                                            <div style={S.obsMeta}>
                                                Balance: ${(o.balance || 0).toFixed(2)} · {o.survival_tier} · {(o.trades || []).length} trades
                                            </div>
                                            <div style={S.obsPreds}>
                                                {Object.entries(op).map(([a, p]) => (
                                                    <span key={a} style={S.predDir(p.direction)}>
                                                        {a} {p.direction === 'up' ? '▲' : p.direction === 'down' ? '▼' : '—'} {pct(p.confidence)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </div>
                </div>
            </div>

            {/* ════ LAST DECISIONS (if any) ════ */}
            {s.last_decisions && s.last_decisions.length > 0 && (
                <div style={S.card}>
                    <div style={S.cardHeader}><h2 style={S.cardHeaderH2}>🧠 Last Decisions</h2></div>
                    <div style={S.cardBody}>
                        <pre style={{ fontFamily: C.fontMono, fontSize: '0.8rem', color: C.textSecondary, whiteSpace: 'pre-wrap', margin: 0 }}>
                            {JSON.stringify(s.last_decisions, null, 2)}
                        </pre>
                    </div>
                </div>
            )}

        </div>
    );
}

export default PolyPage;
